// observability.jsx|tsx
"use client"; // browser only: https://react.dev/reference/react/use-client
import { HoneycombWebSDK } from "@honeycombio/opentelemetry-web";
import { SpanKind, SpanStatusCode, trace } from "@opentelemetry/api";
import { getWebAutoInstrumentations } from "@opentelemetry/auto-instrumentations-web";
import type { ExportResult } from "@opentelemetry/core";
import { ExportResultCode } from "@opentelemetry/core";
import { JsonTraceSerializer } from "@opentelemetry/otlp-transformer";
import type { ReadableSpan, SpanExporter } from "@opentelemetry/sdk-trace-base";
import { api } from "config";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { getBiller } from "selectors";
import { getDeviceFingerprint } from "selectors/appState";

let sdkInitialized = false;

// Extract the base domain from api.ez_pay.hostInstances for trace header propagation
const backendDomain =
  api.ez_pay.hostInstances.split("/ez-pay/v").shift() ||
  api.ez_pay.hostInstances;
const awsWafRegex = /edge\.sdk\.awswaf\.com/; // Regex to match the AWS WAF domain - fixed ReDoS vulnerability
const googleAnalyticsRegex = /google-analytics\.com/; // Regex to match Google Analytics domain - fixed ReDoS vulnerability
const myRegexString =
  String.raw`^(?!https?://[^/]+/ez-pay/v1/` +
  String.raw`(?:payable/(?:prepare|process)(?:$|[/?#])|` +
  String.raw`(?:prepare|process)/[^/?#]+(?:$|[/?#])|` +
  String.raw`validation/krn:spc:[^/]+/lookup(?:$|[/?#])|` +
  String.raw`validation/v2/[^/]+(?:$|[/?#]))` +
  String.raw`).+`;
const ezPayIgnoreRegex = new RegExp(myRegexString); // Regex to match all ez-pay API URLs to be ignored by auto-instrumentation - fixed ReDoS vulnerability
const configDefaults = {
  ignoreNetworkEvents: true,
  ignoreUrls: [awsWafRegex, googleAnalyticsRegex, ezPayIgnoreRegex],
  propagateTraceHeaderCorsUrls: [
    new RegExp(
      `^${backendDomain.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`)}.*`,
    ),
  ],
};

// HTTP context interface matching attributes captured by fetch/XHR auto-instrumentations
export interface HttpContext {
  method?: string; // http.method
  url?: string; // http.url, http.host, http.scheme, http.target
  statusCode?: number; // http.status_code
  responseContentLength?: number; // http.response_content_length
  requestContentLength?: number; // http.request_content_length
}

const buildHttpAttributes = (httpContext: HttpContext) => {
  const attrs: Record<string, any> = {};
  if (httpContext.method)
    attrs["http.method"] = httpContext.method.toUpperCase();
  if (httpContext.url) {
    attrs["http.url"] = httpContext.url;
    try {
      const parsed = new URL(httpContext.url);
      attrs["http.host"] = parsed.host;
      attrs["http.scheme"] = parsed.protocol.replace(":", "");
      attrs["http.target"] = parsed.pathname + parsed.search;
      attrs["net.peer.name"] = parsed.hostname;
      if (parsed.port) attrs["net.peer.port"] = Number(parsed.port);
    } catch {
      /* invalid URL */
    }
  }
  if (httpContext.statusCode !== undefined)
    attrs["http.status_code"] = httpContext.statusCode;
  if (httpContext.responseContentLength !== undefined)
    attrs["http.response_content_length"] = httpContext.responseContentLength;
  if (httpContext.requestContentLength !== undefined)
    attrs["http.request_content_length"] = httpContext.requestContentLength;
  return attrs;
};

const isApiUrl = (url?: string) => {
  if (!url) return false;
  const apiPathRegex = /\/(?:ez-pay\/v\d+|rcp\/v\d*)(?:\/|$)/;
  return apiPathRegex.test(url);
};

const normalizeUrlForIgnoreMatch = (url: string) => {
  try {
    return new URL(url, globalThis.location.origin).toString();
  } catch {
    return url;
  }
};

const shouldCreateHttpErrorSpan = (
  context?: Record<string, any>,
  httpContext?: HttpContext,
) => {
  if (!httpContext) return false;

  const statusCode = httpContext.statusCode;
  const isErrorStatus = typeof statusCode === "number" && statusCode >= 400;
  const hasApiErrorFlag = context?.isApiError === true;

  // Only force span creation for URLs that are suppressed by ignoreUrls.
  // Non-ignored URLs already get spans from auto-instrumentation.
  const normalizedUrl = httpContext.url
    ? normalizeUrlForIgnoreMatch(httpContext.url)
    : undefined;
  const isIgnoredByAutoInstrumentation =
    !!normalizedUrl && ezPayIgnoreRegex.test(normalizedUrl);

  return (
    isApiUrl(httpContext.url) &&
    isIgnoredByAutoInstrumentation &&
    (isErrorStatus || hasApiErrorFlag)
  );
};

// Export tracer and recordException for global error handling
export const getTracer = () => trace.getTracer("ez-pay-ui");

export const recordException = (
  error: any,
  context?: Record<string, any>,
  httpContext?: HttpContext,
) => {
  try {
    const span = trace.getActiveSpan();
    const httpAttrs = httpContext ? buildHttpAttributes(httpContext) : {};
    const shouldForceHttpErrorSpan = shouldCreateHttpErrorSpan(
      context,
      httpContext,
    );
    const errorAttrs = {
      "error.type": error?.constructor?.name || "Unknown",
      "error.message": error?.message || String(error),
      "error.stack": error?.stack,
      ...httpAttrs,
      ...context,
    };
    const errorMessage = error?.message || String(error);

    if (shouldForceHttpErrorSpan) {
      const tracer = getTracer();
      const httpSpanName = httpContext?.method
        ? `HTTP ${httpContext.method.toUpperCase()}`
        : "HTTP";
      const httpErrorSpan = tracer.startSpan(httpSpanName, {
        kind: SpanKind.CLIENT,
      });
      httpErrorSpan.setAttributes(errorAttrs);
      httpErrorSpan.recordException(error);
      httpErrorSpan.setStatus({
        code: SpanStatusCode.ERROR,
        message: errorMessage,
      });
      httpErrorSpan.end();

      const errorSpan = tracer.startSpan("error", {
        kind: SpanKind.INTERNAL,
      });
      errorSpan.setAttributes(errorAttrs);
      errorSpan.recordException(error);
      errorSpan.setStatus({
        code: SpanStatusCode.ERROR,
        message: errorMessage,
      });
      errorSpan.end();
    } else if (span) {
      // Attach to current active span if one exists
      span.setAttributes(errorAttrs);
      span.recordException(error);
      span.setStatus({ code: SpanStatusCode.ERROR, message: errorMessage });
    } else {
      // No active span: create an error span only.
      // For non-ignored URLs the HTTP span already exists from auto-instrumentation,
      // so we must not duplicate it with another HTTP {METHOD} span.
      const tracer = getTracer();
      const newSpan = tracer.startSpan("error", { kind: SpanKind.INTERNAL });
      newSpan.setAttributes(errorAttrs);
      newSpan.recordException(error);
      newSpan.setStatus({ code: SpanStatusCode.ERROR, message: errorMessage });
      newSpan.end();
    }
  } catch (e) {
    console.error("Failed to record exception to Honeycomb:", e);
  }
};

// The OTLP JSON serializer emits integer attribute values (e.g. screen.width,
// SampleRate, browser.width) as bare JSON numbers, but the backend's OTLP
// receiver requires int64 values encoded as strings per the protobuf JSON
// mapping spec. Patch the serialized body rather than the SDK internals so
// every numeric attribute is covered regardless of where it's set.
class QuotedIntValueTraceExporter implements SpanExporter {
  constructor(private readonly url: string) {}

  export(
    spans: ReadableSpan[],
    resultCallback: (result: ExportResult) => void,
  ): void {
    const bytes = JsonTraceSerializer.serializeRequest(spans);
    const body = new TextDecoder()
      .decode(bytes)
      .replace(/"intValue":(-?\d+)/g, '"intValue":"$1"');

    fetch(this.url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    })
      .then(() => resultCallback({ code: ExportResultCode.SUCCESS }))
      .catch((error) =>
        resultCallback({ code: ExportResultCode.FAILED, error }),
      );
  }

  shutdown(): Promise<void> {
    return Promise.resolve();
  }
}

export default function Observability() {
  const initRef = useRef(false);
  const biller = useSelector(getBiller);
  const deviceFingerprint = useSelector(getDeviceFingerprint);
  const initialBillerRef = useRef(biller);
  const deviceFingerprintRef = useRef(deviceFingerprint);

  useEffect(() => {
    // Prevent multiple initializations
    if (initRef.current || sdkInitialized) {
      return;
    }

    try {
      const sdk = new HoneycombWebSDK({
        tracesEndpoint: `${api.ez_pay.hostInstances}/api/logs`,
        disableDefaultTraceExporter: true,
        traceExporter: new QuotedIntValueTraceExporter(
          `${api.ez_pay.hostInstances}/api/logs`,
        ),
        debug: false, // Set to false for production environment.
        serviceName: "ez-pay-ui",
        instrumentations: [
          getWebAutoInstrumentations({
            // Loads custom configuration for xml-http-request instrumentation.
            "@opentelemetry/instrumentation-xml-http-request": configDefaults,
            "@opentelemetry/instrumentation-fetch": configDefaults,
            "@opentelemetry/instrumentation-document-load": { enabled: false },
            "@opentelemetry/instrumentation-user-interaction": {
              enabled: false,
            }, // Disable user interaction instrumentation to reduce noise from clicks
          }),
        ],
        webVitalsInstrumentationConfig: {
          vitalsToTrack: [],
        },
        resourceAttributes: {
          "ezpay.releaseVersion": process.env.APP_VERSION,
          "ezpay.plus":
            initialBillerRef.current?.featureFlags?.payableUi?.toLowerCase() ===
            "true",
          ...(initialBillerRef.current?.billerName
            ? { "ezpay.biller.name": initialBillerRef.current.billerName }
            : {}),
          ...(initialBillerRef.current?.realm
            ? { "ezpay.biller.realm": initialBillerRef.current.realm }
            : {}),
          ...(initialBillerRef.current?.spaceKrn
            ? { "ezpay.biller.space_krn": initialBillerRef.current.spaceKrn }
            : {}),
          ...(deviceFingerprintRef.current
            ? { "ezpay.device.fingerprint": deviceFingerprintRef.current }
            : {}),
        },
      });
      sdk.start();
      initRef.current = true;
      sdkInitialized = true;
    } catch (e) {
      console.error("Failed to initialize Honeycomb SDK:", e);
    }
  }, []);

  return null;
}
